import { Link } from 'react-router-dom'
import { useState } from 'react'

// Mantine
import { Anchor, Slider } from '@mantine/core'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// UI
import Box from '../ui/Box'
import SKU from '../ui/SKU'

// User
import useMe from '@/hooks/useMe'

// Icons
import { IconCheck, IconX } from '@tabler/icons-react'

// Seuils de couverture
const ZONE_RED = 3 // 0-3 mois = critique
const ZONE_YELLOW = 8 // 3-8 mois = attention

function getCoverageColor(months) {
    if (months < ZONE_RED) return 'red'
    if (months < ZONE_YELLOW) return 'yellow'
    return 'green'
}

const MAX_MONTHS = 12 // Maximum pour la jauge

// Jauge style HomeKit - arc semi-circulaire
function HomeKitGauge({ months }) {
    const color = getCoverageColor(months)
    const percentage = Math.min(months / MAX_MONTHS, 1) * 100

    // Couleurs selon le statut
    const colorMap = {
        red: { stroke: '#ef4444', text: '#dc2626' },
        yellow: { stroke: '#f97316', text: '#ea580c' },
        green: { stroke: '#22c55e', text: '#16a34a' },
    }

    const colors = colorMap[color]

    // Arc semi-circulaire (180°)
    const size = 120
    const strokeWidth = 10
    const radius = (size - strokeWidth) / 2
    const circumference = Math.PI * radius // Demi-cercle

    const strokeDashoffset = circumference * (1 - percentage / 100)

    return (
        <div className="relative inline-flex flex-col items-center">
            <svg
                width={size}
                height={size / 2 + 10}
                viewBox={`0 0 ${size} ${size / 2 + 10}`}
            >
                {/* Arc de fond */}
                <path
                    d={`M ${strokeWidth / 2} ${
                        size / 2
                    } A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${
                        size / 2
                    }`}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />
                {/* Arc de progression */}
                <path
                    d={`M ${strokeWidth / 2} ${
                        size / 2
                    } A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${
                        size / 2
                    }`}
                    fill="none"
                    stroke={colors.stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                        transition: 'stroke-dashoffset 0.5s ease-out',
                    }}
                />
            </svg>
            {/* Texte central */}
            <div
                className="absolute flex flex-col items-center"
                style={{ bottom: '0px' }}
            >
                <span
                    className="text-3xl font-bold leading-none"
                    style={{ color: colors.text }}
                >
                    {months}
                </span>
                <span className="text-xs text-gray-500">mois</span>
            </div>
        </div>
    )
}

export default function ComponentCoverage({ component, onUpdated }) {
    const { isAdmin } = useMe()
    const { requestPatch } = useAxiosApi()

    const [isKeepInStock, setIsKeepInStock] = useState(
        component.is_keep_in_stock ?? true
    )
    const [updating, setUpdating] = useState(false)
    const [targetMonths, setTargetMonths] = useState(6)

    const toggleKeepInStock = async () => {
        setUpdating(true)
        const newValue = !isKeepInStock

        try {
            await requestPatch(`/components/${component.id}`, {
                is_keep_in_stock: newValue,
            })

            setIsKeepInStock(newValue)
            if (onUpdated) onUpdated()
        } finally {
            setUpdating(false)
        }
    }

    const hasChildren = component.children.length > 0
    const hasParents = component.parents.length > 0
    const hasShopifyVariants = component.shopify_product_variants.length > 0

    const coverageMonths = Math.floor(component.coverage_days / 30)

    // Cas d'un composant sans parents : sa couverture repose sur ses enfants
    // (l'enfant avec la couverture la plus faible détermine la couverture du parent)
    const dependsOnChildren = hasChildren && !hasParents

    // Trouver les enfants limitants
    // Afficher tous ceux avec couverture <= 6 mois, ou à défaut les 3 premiers
    const sortedChildren = dependsOnChildren
        ? component.children
              .filter((child) => child.coverage_days != null)
              .sort((a, b) => a.coverage_days - b.coverage_days)
        : []

    const criticalChildren = sortedChildren.filter(
        (child) => child.coverage_days <= 6 * 30
    )

    const limitingChildren =
        criticalChildren.length > 0
            ? criticalChildren
            : sortedChildren.slice(0, 3)

    // Ne pas afficher si pas admin, pas d'enfants/parents, ou pas de coverage
    if (
        !isAdmin ||
        (!hasChildren && !hasParents && !hasShopifyVariants) ||
        !component.coverage_days === null
    ) {
        return null
    }

    // Écoulement (uniquement coverage_flow_rate)
    const flowRate = component.coverage_flow_rate || 0
    const monthlyFlowRate = Math.round(flowRate * 30)

    // Afficher stock et écoulement seulement si le composant a des parents ou variantes Shopify
    // ET n'a pas d'enfants (les composants avec enfants reposent sur leurs enfants, pas sur leur propre stock/écoulement)
    const hasFlowData = (hasParents || hasShopifyVariants) && !hasChildren

    const rightSection = (
        <button
            onClick={toggleKeepInStock}
            disabled={updating}
            className={`
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border
                ${
                    isKeepInStock
                        ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                }
                ${updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
        >
            {isKeepInStock ? (
                <IconCheck size={14} stroke={2.5} />
            ) : (
                <IconX size={14} stroke={2} />
            )}
            {isKeepInStock ? 'Suivi actif' : 'Suivi désactivé'}
        </button>
    )

    return (
        <Box title="Couverture de stock" rightSection={rightSection}>
            {/* Jauge centrée */}
            <div className="flex justify-center mb-4">
                <HomeKitGauge months={coverageMonths} />
            </div>

            {/* Stock et écoulement */}
            {hasFlowData && (
                <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                    <div className="text-center">
                        <div className="text-gray-500 text-xs mb-1">
                            {hasParents
                                ? 'Stock actuel + déjà assemblés + en bons de commande'
                                : 'Stock actuel'}
                        </div>
                        <div className="font-semibold text-lg">
                            {component.coverage_quantity?.toLocaleString() || 0}{' '}
                            unités
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-gray-500 text-xs mb-1">
                            Écoulement
                        </div>
                        <div className="font-semibold text-lg">
                            {monthlyFlowRate} unités/mois
                        </div>
                    </div>
                </div>
            )}

            {/* Composants limitants */}
            {limitingChildren.length > 0 && (
                <div
                    className={`text-sm ${
                        hasFlowData ? 'border-t pt-4 mt-4' : ''
                    }`}
                >
                    <div className="text-gray-500 text-xs mb-2">
                        Couverture limitée par
                    </div>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-2 px-3 font-medium">
                                        Composant
                                    </th>
                                    <th className="text-right py-2 px-3 font-medium w-24">
                                        Couverture
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {limitingChildren.map((cc, index) => {
                                    const childMonths = Math.floor(
                                        cc.coverage_days / 30
                                    )
                                    const childColor =
                                        getCoverageColor(childMonths)
                                    const isLast =
                                        index === limitingChildren.length - 1

                                    return (
                                        <tr
                                            key={cc.id}
                                            className={`
                                                ${
                                                    index % 2 === 1
                                                        ? 'bg-gray-50'
                                                        : ''
                                                }
                                                ${
                                                    !isLast
                                                        ? 'border-b border-gray-100'
                                                        : ''
                                                }
                                            `}
                                        >
                                            <td className="py-2 px-3">
                                                <div className="flex items-center gap-2">
                                                    <SKU
                                                        component={cc}
                                                        size="sm"
                                                    />
                                                    <Link
                                                        to={`/components/${cc.id}`}
                                                    >
                                                        <Anchor
                                                            size="sm"
                                                            component="span"
                                                        >
                                                            {cc
                                                                .component_category
                                                                ?.name_fr +
                                                                ' — ' +
                                                                cc.name}
                                                        </Anchor>
                                                    </Link>
                                                </div>
                                            </td>
                                            <td className="py-2 px-3 text-right">
                                                <span
                                                    className={`font-semibold ${
                                                        childColor === 'red'
                                                            ? 'text-red-500'
                                                            : childColor ===
                                                                'yellow'
                                                              ? 'text-orange-500'
                                                              : 'text-green-500'
                                                    }`}
                                                >
                                                    {childMonths} mois
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Calculateur de stock nécessaire */}
            {hasFlowData && monthlyFlowRate > 0 && (
                <div className="border-t pt-4 mt-4">
                    <div className="text-gray-500 text-xs mb-3">
                        Calculateur de couverture
                    </div>
                    <div className="mb-4">
                        <Slider
                            value={targetMonths}
                            onChange={setTargetMonths}
                            min={3}
                            max={12}
                            step={1}
                            marks={[
                                { value: 3, label: '3' },
                                { value: 4, label: '4' },
                                { value: 5, label: '5' },
                                { value: 6, label: '6' },
                                { value: 7, label: '7' },
                                { value: 8, label: '8' },
                                { value: 9, label: '9' },
                                { value: 10, label: '10' },
                                { value: 11, label: '11' },
                                { value: 12, label: '12' },
                            ]}
                            label={(value) => `${value} mois`}
                            size="sm"
                        />
                    </div>
                    <div className="mt-6 p-3 bg-gray-50 rounded-lg text-center text-sm text-gray-600">
                        {(() => {
                            const neededTotal = monthlyFlowRate * targetMonths
                            const currentStock =
                                component.coverage_quantity || 0
                            const toOrder = Math.max(
                                0,
                                neededTotal - currentStock
                            )

                            if (coverageMonths >= targetMonths) {
                                return (
                                    <span>
                                        Objectif déjà atteint ({coverageMonths}{' '}
                                        mois actuellement)
                                    </span>
                                )
                            }

                            return (
                                <span>
                                    Commander {toOrder.toLocaleString()} unités
                                    pour tenir {targetMonths} mois
                                </span>
                            )
                        })()}
                    </div>
                </div>
            )}
        </Box>
    )
}
